import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import BopBackLink from '../../../components/BopBackLink';
import { apiFetch } from '../../../lib/api';
import { showToast } from '../../../components/Toast';
import { useDirtyForm } from '../../../hooks/useDirtyForm';
import { useBloccoRecordContesto } from '../../../hooks/useBloccoRecordContesto';
import NominativoClientePicker from '../../../components/NominativoClientePicker';
import GestioneAllegatiAcquirente from '../../../components/GestioneAllegatiAcquirente';
import '../capitolati/OfferteCapitolatoPage.css';

import TabRiepilogo   from './tab/TabRiepilogo';
import TabOrdini      from './tab/TabOrdini';
import TabPreventivi  from './tab/TabPreventivi';
import TabDistinte    from './tab/TabDistinte';
import TabSal         from './tab/TabSal';
import TabAcconti     from './tab/TabAcconti';
import TabFatture     from './tab/TabFatture';
import TabDocumenti   from './tab/TabDocumenti';
import TabTeam        from './tab/TabTeam';
import PrintModal from '../../../components/PrintModal';
import { ModalNuovoPreventivo } from '../attivo/PreventiviPage';
import { useRiepilogoPercorso, CellaPercorso } from '../percorso/RiepilogoPercorsoCommessa';

// ── Tipi ─────────────────────────────────────────────────────────────────────

type StatoCommessa = 'BOZZA' | 'IN_CORSO' | 'COMPLETATA' | 'CHIUSA';

export interface CommessaDettaglio {
  id: number;
  numero: string;
  descrizione: string;
  clienteRagioneSociale: string;
  clientePartitaIva?: string | null;
  nominativoId?: number | null;
  stato: StatoCommessa;
  societaId: number;
  societaNome?: string;
  sedeId: number;
  sedeNome?: string;
  responsabileId: number;
  responsabileNome?: string;
  responsabile?: { id: number; nome: string; cognome: string | null; email: string };
  condizionePagamentoId?: number | null;
  condizionePagamento?: { id: number; nome: string; canale: string } | null;
  depositoId?: number | null;
  offertaCapitolatoId?: number | null;
  budgetIniziale?: number | null;
  indirizzoLavori?: string | null;
  dataApertura: string;
  dataChiusura?: string | null;
  kpi: {
    totaleOrdini: number;
    nOrdini: number;
    nDistinte: number;
    nConfermate: number;
    totaleSal: number;
    nSal: number;
    nFatturati: number;
  };
  team: { id: number; utenteId: number; nomeCompleto: string; email: string; aggiuntoAt: string; }[];
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const STATO_CONFIG: Record<StatoCommessa, { label: string; cls: string }> = {
  BOZZA:      { label: 'Bozza',      cls: 'bop-badge--neutral'  },
  IN_CORSO:   { label: 'In Corso',   cls: 'bop-badge--info'     },
  COMPLETATA: { label: 'Completata', cls: 'bop-badge--success'  },
  CHIUSA:     { label: 'Chiusa',     cls: 'bop-scheda-commessa__badge-chiusa' },
};

const TRANSIZIONI: Record<StatoCommessa, StatoCommessa[]> = {
  BOZZA:      ['IN_CORSO'],
  IN_CORSO:   ['COMPLETATA', 'CHIUSA'],
  COMPLETATA: ['CHIUSA'],
  CHIUSA:     [],
};

const TAB_LABELS = [
  'Riepilogo','Preventivi','Ordini Cliente','Acconti','Distinte',
  'SAL','Fatture','Costi','Magazzino','Subappalti',
  'Documenti','Team','Capitolato',
];

// ── Placeholder Tab ───────────────────────────────────────────────────────────

const PLACEHOLDER_ICONS: Record<string, string> = {
  'Acconti': '💳', 'Fatture': '🧾', 'Costi': '📊',
  'Magazzino': '📦', 'Subappalti': '🔨',
};

function TabPlaceholder({ label }: { label: string }) {
  return (
    <div className="bop-scheda-commessa__placeholder">
      <div className="bop-scheda-commessa__placeholder-icon">{PLACEHOLDER_ICONS[label] ?? '🔧'}</div>
      <h3 className="bop-scheda-commessa__placeholder-title">Sezione in sviluppo</h3>
      <p className="bop-scheda-commessa__placeholder-text">
        La sezione <strong>{label}</strong> sarà disponibile in un prossimo aggiornamento.
      </p>
    </div>
  );
}

// ── Tab Capitolato ────────────────────────────────────────────────────────────

interface OffertaCapitolatoReadonly {
  id: number;
  codice: string;
  stato: string;
  oggetto: string;
  testo: string | null;
  clienteNome: string;
  clienteIndirizzo: string | null;
  clienteCap: string | null;
  clienteCitta: string | null;
  clienteProvincia: string | null;
  clientePiva: string | null;
  clienteCf: string | null;
  createdAt: string;
  ricarico: string | null;
  tipoFatturazione: 'IMPRESA_PAGA_TUTTO' | 'CLIENTE_PAGA_DIRETTAMENTE' | null;
  righe: {
    id: number;
    ordine: number;
    codicePrecopy: string;
    nomePrecopy: string;
    umVenditaPrecopy: string;
    prezzoUnitario: string;
    scorporo: string | null;
    descrizioneRiga: string | null;
  }[];
  righeAccessorie: {
    id: number;
    ordine: number;
    codicePrecopy: string;
    nomePrecopy: string;
    tipologiaPrecopy: string;
    umVenditaPrecopy: string;
    prezzoUnitario: string;
    scorporo: string | null;
    descrizioneRiga: string | null;
  }[];
}

interface CommessaAcquirente {
  id: number;
  commessaId: number;
  unitaCodice: string;
  unitaDescrizione: string | null;
  nome: string;
  cognome: string;
  telefono: string | null;
  email: string | null;
  note: string | null;
  createdAt: string;
  updatedAt: string;
  numAllegati: number;
  numPreventivi: number;
}

interface FormAcq {
  unitaCodice: string;
  unitaDescrizione: string;
  nome: string;
  cognome: string;
  telefono: string;
  email: string;
  note: string;
}

const FORM_AQC_EMPTY: FormAcq = {
  unitaCodice: '', unitaDescrizione: '',
  nome: '', cognome: '',
  telefono: '', email: '', note: '',
};

// ── Icone SVG per le celle contatori in tabella acquirenti ──────────────────

const PaperclipIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
  </svg>
);

const FileTextIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
    <polyline points="10 9 9 9 8 9" />
  </svg>
);

// ── Modale dedicata: gestione allegati acquirente ──────────────────────────

function ModaleAllegatiAcquirenteDedicata({
  acquirente,
  onClose,
  onChange,
}: {
  acquirente: CommessaAcquirente;
  onClose: () => void;
  onChange: () => void;
}) {
  return (
    <div className="bop-modal-overlay">
      <div className="bop-modal bop-modal--lg">
        <div className="bop-modal__header">
          <h3 className="bop-modal__title">
            Allegati acquirente · {acquirente.unitaCodice} — {acquirente.nome} {acquirente.cognome}
          </h3>
          <button className="bop-modal__close" onClick={onClose}>×</button>
        </div>
        <div className="bop-modal__body">
          <GestioneAllegatiAcquirente
            acquirenteId={acquirente.id}
            acquirenteUnita={acquirente.unitaCodice}
            acquirenteNomeCompleto={`${acquirente.nome} ${acquirente.cognome}`}
            onChange={onChange}
          />
        </div>
        <div className="bop-modal__footer">
          <button className="bop-btn bop-btn-secondary" onClick={onClose}>Chiudi</button>
        </div>
      </div>
    </div>
  );
}

function TabCapitolato({
  commessa,
  onApriPreventiviAcquirente,
}: {
  commessa: CommessaDettaglio;
  onApriPreventiviAcquirente: (acquirenteId: number, label: string) => void;
}) {
  // Sub-tab
  const [subTab, setSubTab] = useState<'parametri' | 'acquirenti'>('parametri');

  // Offerta capitolato
  const [offerta, setOfferta] = useState<OffertaCapitolatoReadonly | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Acquirenti
  const [acquirenti, setAcquirenti] = useState<CommessaAcquirente[]>([]);
  const riepilogoPercorso = useRiepilogoPercorso(commessa?.id, acquirenti.length);
  const [loadingAcq, setLoadingAcq] = useState(false);
  const [modalAcq, setModalAcq] = useState(false);
  const [editingAcq, setEditingAcq] = useState<CommessaAcquirente | null>(null);
  const [formAcq, setFormAcq] = useState<FormAcq>(FORM_AQC_EMPTY);
  const [savingAcq, setSavingAcq] = useState(false);
  const [errorAcq, setErrorAcq] = useState('');
  const [deletingAcqId, setDeletingAcqId] = useState<number | null>(null);

  // Modale dedicata allegati
  const [modalAllegatiAcqId, setModalAllegatiAcqId] = useState<number | null>(null);

  useEffect(() => {
    if (!commessa.offertaCapitolatoId) return;
    setLoading(true);
    setError('');
    // @api GET /api/offerte-capitolato/:id -> {"success":true,"data":{"id":"number","codice":"string","righe":[]}}
    apiFetch<{ success: boolean; data: OffertaCapitolatoReadonly }>(
      `/api/offerte-capitolato/${commessa.offertaCapitolatoId}`
    )
      .then(res => setOfferta(res.data))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [commessa.offertaCapitolatoId]);

  useEffect(() => {
    if (!commessa?.id) return;
    setLoadingAcq(true);
    // @api GET /api/commesse/:id/acquirenti -> {"success":true,"data":[...]}
    apiFetch<{ success: boolean; data: CommessaAcquirente[] }>(
      `/api/commesse/${commessa.id}/acquirenti`
    )
      .then(res => setAcquirenti(res.data))
      .catch(() => {})
      .finally(() => setLoadingAcq(false));
  }, [commessa?.id]);

  const ricaricaAcquirenti = () => {
    // @api GET /api/commesse/:id/acquirenti -> {"success":true,"data":[...]}
    apiFetch<{ success: boolean; data: CommessaAcquirente[] }>(
      `/api/commesse/${commessa.id}/acquirenti`
    )
      .then(res => setAcquirenti(res.data))
      .catch(() => {});
  };

  const apriNuovoAcquirente = () => {
    setEditingAcq(null);
    setFormAcq(FORM_AQC_EMPTY);
    setErrorAcq('');
    setModalAcq(true);
  };

  const apriModificaAcquirente = (a: CommessaAcquirente) => {
    setEditingAcq(a);
    setFormAcq({
      unitaCodice:      a.unitaCodice,
      unitaDescrizione: a.unitaDescrizione ?? '',
      nome:             a.nome,
      cognome:          a.cognome,
      telefono:         a.telefono ?? '',
      email:            a.email ?? '',
      note:             a.note ?? '',
    });
    setErrorAcq('');
    setModalAcq(true);
  };

  const salvaAcquirente = async () => {
    if (!formAcq.unitaCodice.trim()) { setErrorAcq('Il codice unità è obbligatorio'); return; }
    if (!formAcq.nome.trim() || !formAcq.cognome.trim()) { setErrorAcq('Nome e cognome sono obbligatori'); return; }
    setSavingAcq(true);
    setErrorAcq('');
    const body = {
      unitaCodice:      formAcq.unitaCodice.trim(),
      unitaDescrizione: formAcq.unitaDescrizione.trim() || null,
      nome:             formAcq.nome.trim(),
      cognome:          formAcq.cognome.trim(),
      telefono:         formAcq.telefono.trim() || null,
      email:            formAcq.email.trim() || null,
      note:             formAcq.note.trim() || null,
    };
    try {
      if (editingAcq) {
        // @api PUT /api/commesse/:id/acquirenti/:acquirenteId -> {"success":true}
        await apiFetch(`/api/commesse/${commessa.id}/acquirenti/${editingAcq.id}`, {
          method: 'PUT', body: JSON.stringify(body),
        });
      } else {
        // @api POST /api/commesse/:id/acquirenti -> {"success":true,"data":{...}}
        await apiFetch(`/api/commesse/${commessa.id}/acquirenti`, {
          method: 'POST', body: JSON.stringify(body),
        });
      }
      ricaricaAcquirenti();
      setModalAcq(false);
    } catch (e: any) {
      setErrorAcq(e.message);
    } finally {
      setSavingAcq(false);
    }
  };

  const eliminaAcquirente = (id: number) => {
    setDeletingAcqId(id);
    // @api DELETE /api/commesse/:id/acquirenti/:acquirenteId -> {"success":true}
    apiFetch(`/api/commesse/${commessa.id}/acquirenti/${id}`, { method: 'DELETE' })
      .then(() => setAcquirenti(prev => prev.filter(a => a.id !== id)))
      .catch(e => showToast((e as Error).message, 'error'))
      .finally(() => setDeletingAcqId(null));
  };

  return (
    <>
      {/* Sub-tab bar — sempre visibile */}
      <div className="ofc-subtab-bar">
        <button
          className={`ofc-subtab ${subTab === 'parametri' ? 'ofc-subtab--active' : ''}`}
          onClick={() => setSubTab('parametri')}>
          Parametri capitolato
        </button>
        <button
          className={`ofc-subtab ${subTab === 'acquirenti' ? 'ofc-subtab--active' : ''}`}
          onClick={() => setSubTab('acquirenti')}>
          Acquirenti
          {acquirenti.length > 0 && (
            <span className="ofc-subtab-badge">{acquirenti.length}</span>
          )}
        </button>
      </div>

      {/* ── Sub-tab: Parametri capitolato ── */}
      {subTab === 'parametri' && (
        <>
          {!commessa.offertaCapitolatoId && (
            <div className="ofc-tab-no-offerta">
              Questa commessa non è stata generata da un'offerta capitolato.
            </div>
          )}
          {commessa.offertaCapitolatoId && loading && (
            <div className="bop-empty-state">Caricamento offerta...</div>
          )}
          {commessa.offertaCapitolatoId && !loading && (error || !offerta) && (
            <div className="bop-empty-state">{error || 'Offerta non trovata'}</div>
          )}
          {commessa.offertaCapitolatoId && !loading && !error && offerta && (() => {
            const totalePreconf = offerta.righe.reduce(
              (sum, r) => sum + parseFloat(r.prezzoUnitario), 0
            );
            const totaleAcc = (offerta.righeAccessorie || []).reduce(
              (sum, r) => sum + parseFloat(r.prezzoUnitario), 0
            );
            const totale = totalePreconf + totaleAcc;
            return (
              <div className="ofc-tab-content">
                <div className="ofc-tab-cards">
                  <div className="ofc-tab-card">
                    <div className="ofc-tab-card__label">Cliente</div>
                    <div className="ofc-tab-card__value">{offerta.clienteNome}</div>
                    {offerta.clienteIndirizzo && (
                      <div className="bop-text-muted">
                        {[offerta.clienteIndirizzo, offerta.clienteCap, offerta.clienteCitta, offerta.clienteProvincia]
                          .filter(Boolean).join(', ')}
                      </div>
                    )}
                  </div>
                  <div className="ofc-tab-card">
                    <div className="ofc-tab-card__label">Oggetto</div>
                    <div className="ofc-tab-card__value">{offerta.oggetto}</div>
                    <div className="bop-text-muted">
                      <span className="ofc-codice-badge">{offerta.codice}</span>
                    </div>
                  </div>
                  <div className="ofc-tab-card">
                    <div className="ofc-tab-card__label">Data offerta</div>
                    <div className="ofc-tab-card__value">
                      {new Date(offerta.createdAt).toLocaleDateString('it-IT')}
                    </div>
                    <div className="bop-text-muted">
                      Totale: € {totale.toFixed(2)}
                      {totaleAcc > 0 && (
                        <span> (di cui accessori: € {totaleAcc.toFixed(2)})</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Sezione: Ricarichi e fatturazione */}
                {(offerta.ricarico != null || offerta.tipoFatturazione != null) && (
                  <div className="ofc-tab-ricarico-section">
                    <div className="ofc-tab-righe-header">Ricarichi impresa e regole di fatturazione</div>
                    <div className="ofc-tab-ricarico-cards">
                      {offerta.ricarico != null && (
                        <div className="ofc-tab-ricarico-card">
                          <div className="ofc-tab-card__label">Ricarico impresa su extra-capitolati</div>
                          <div className="ofc-tab-card__value ofc-tab-ricarico-value">
                            {parseFloat(offerta.ricarico).toFixed(2)}%
                          </div>
                          <div className="bop-text-muted">
                            Percentuale applicata dall'impresa al cliente finale
                            sulla differenza extra-capitolato
                          </div>
                        </div>
                      )}
                      {offerta.tipoFatturazione != null && (
                        <div className="ofc-tab-ricarico-card">
                          <div className="ofc-tab-card__label">Regola di fatturazione</div>
                          {offerta.tipoFatturazione === 'IMPRESA_PAGA_TUTTO' ? (
                            <>
                              <div className="ofc-tab-card__value">
                                L'impresa gestisce e incassa gli extra
                              </div>
                              <div className="bop-text-muted">
                                A noi: capitolato + extra senza ricarico.
                                L'impresa incassa dal cliente finale (con ricarico).
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="ofc-tab-card__value">
                                Il cliente finale paga gli extra direttamente a noi
                              </div>
                              <div className="bop-text-muted">
                                Il cliente paga a noi extra + ricarico.
                                L'incasso viene dedotto dal capitolato dovuto dall'impresa.
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {offerta.testo && (
                  <div className="ofc-tab-testo">{offerta.testo}</div>
                )}

                <div className="ofc-tab-righe-header">
                  Righe offerta capitolato ({offerta.righe.length})
                </div>
                <div className="bop-card">
                  <table className="bop-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Codice</th>
                        <th>Nome</th>
                        <th>UM</th>
                        <th>Prezzo unit.</th>
                        <th>Scorporo</th>
                        <th>Descrizione</th>
                      </tr>
                    </thead>
                    <tbody>
                      {offerta.righe.length === 0 ? (
                        <tr>
                          <td colSpan={7}>
                            <div className="bop-empty-state">Nessuna riga</div>
                          </td>
                        </tr>
                      ) : (
                        offerta.righe.map((r, idx) => (
                          <tr key={r.id}>
                            <td>{idx + 1}</td>
                            <td><span className="ofc-codice-badge">{r.codicePrecopy}</span></td>
                            <td>{r.nomePrecopy}</td>
                            <td>{r.umVenditaPrecopy}</td>
                            <td>€ {parseFloat(r.prezzoUnitario).toFixed(2)}</td>
                            <td>
                              {r.scorporo != null && parseFloat(r.scorporo) > 0
                                ? `€ ${parseFloat(r.scorporo).toFixed(2)}`
                                : <span className="bop-text-muted">—</span>}
                            </td>
                            <td>{r.descrizioneRiga || <span className="bop-text-muted">—</span>}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {(offerta.righeAccessorie?.length ?? 0) > 0 && (
                  <>
                    <div className="ofc-tab-righe-header ofc-tab-righe-header--mt">
                      Articoli accessori aggiuntivi ({offerta.righeAccessorie.length})
                    </div>
                    <div className="bop-card">
                      <table className="bop-table">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Codice</th>
                            <th>Nome</th>
                            <th>Tipologia</th>
                            <th>UM</th>
                            <th>Prezzo unit.</th>
                            <th>Scorporo</th>
                            <th>Descrizione</th>
                          </tr>
                        </thead>
                        <tbody>
                          {offerta.righeAccessorie.map((r, idx) => (
                            <tr key={r.id}>
                              <td>{idx + 1}</td>
                              <td><span className="ofc-codice-badge">{r.codicePrecopy}</span></td>
                              <td>{r.nomePrecopy}</td>
                              <td><span className="bop-text-muted">{r.tipologiaPrecopy}</span></td>
                              <td>{r.umVenditaPrecopy}</td>
                              <td>€ {parseFloat(r.prezzoUnitario).toFixed(2)}</td>
                              <td>
                                {r.scorporo != null && parseFloat(r.scorporo) > 0
                                  ? `€ ${parseFloat(r.scorporo).toFixed(2)}`
                                  : <span className="bop-text-muted">—</span>}
                              </td>
                              <td>{r.descrizioneRiga || <span className="bop-text-muted">—</span>}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            );
          })()}
        </>
      )}

      {/* ── Sub-tab: Acquirenti ── */}
      {subTab === 'acquirenti' && (
        <>
          <div className="ofc-acquirenti-banner">
            Gli acquirenti sono i clienti finali delle unità abitative.
            I preventivi degli extra-capitolato possono essere intestati
            direttamente a loro. Se le regole di fatturazione lo prevedono,
            l'acquirente diventa cliente diretto per la propria quota extra-capitolato.
          </div>

          <div className="ofc-acquirenti-header">
            <span>{acquirenti.length} acquirent{acquirenti.length === 1 ? 'e' : 'i'} registrat{acquirenti.length === 1 ? 'o' : 'i'}</span>
            <button className="bop-btn bop-btn-primary" onClick={apriNuovoAcquirente}>
              + Aggiungi acquirente
            </button>
          </div>

          <div className="bop-card">
            {loadingAcq ? (
              <div className="bop-empty-state">Caricamento acquirenti...</div>
            ) : acquirenti.length === 0 ? (
              <div className="bop-empty-state">Nessun acquirente registrato</div>
            ) : (
              <table className="bop-table">
                <thead>
                  <tr>
                    <th>Unità</th>
                    <th>Acquirente</th>
                    <th>Telefono</th>
                    <th>Email</th>
                    <th className="bop-text-center">Preventivi</th>
                    <th className="bop-text-center">Allegati</th>
                    <th>Percorso</th>
                    <th>Note</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {acquirenti.map(a => (
                    <tr key={a.id}>
                      <td>
                        <strong>{a.unitaCodice}</strong>
                        {a.unitaDescrizione && (
                          <><br /><span className="bop-text-muted">{a.unitaDescrizione}</span></>
                        )}
                      </td>
                      <td>{a.nome} {a.cognome}</td>
                      <td>{a.telefono || '—'}</td>
                      <td>{a.email || '—'}</td>
                      <td className="bop-text-center">
                        {a.numPreventivi === 0 ? (
                          <span className="bop-acq-cell-counter bop-acq-cell-counter--zero">—</span>
                        ) : (
                          <button
                            className="bop-acq-cell-counter"
                            onClick={() => onApriPreventiviAcquirente(
                              a.id,
                              `${a.unitaCodice} — ${a.nome} ${a.cognome}`
                            )}
                            title={`Vai ai ${a.numPreventivi} preventivi di ${a.nome} ${a.cognome}`}
                          >
                            <FileTextIcon />
                            <span>{a.numPreventivi}</span>
                          </button>
                        )}
                      </td>
                      <td className="bop-text-center">
                        {a.numAllegati === 0 ? (
                          <span className="bop-acq-cell-counter bop-acq-cell-counter--zero" title="Nessun allegato">
                            <PaperclipIcon />
                            <span>0</span>
                          </span>
                        ) : (
                          <button
                            className="bop-acq-cell-counter"
                            onClick={() => setModalAllegatiAcqId(a.id)}
                            title={`Gestisci ${a.numAllegati} allegati`}
                          >
                            <PaperclipIcon />
                            <span>{a.numAllegati}</span>
                          </button>
                        )}
                      </td>
                      <td><CellaPercorso riepilogo={riepilogoPercorso.get(a.id)} acquirenteId={a.id} /></td>
                      <td><span className="bop-text-muted">{a.note || '—'}</span></td>
                      <td>
                        <button
                          className="bop-btn bop-btn-secondary bop-btn--sm"
                          onClick={() => apriModificaAcquirente(a)}>
                          Modifica
                        </button>
                        {' '}
                        <button
                          className="bop-btn bop-btn-danger bop-btn--sm"
                          onClick={() => eliminaAcquirente(a.id)}
                          disabled={deletingAcqId === a.id}>
                          {deletingAcqId === a.id ? '...' : 'Elimina'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {/* ── Modale aggiunta / modifica acquirente ── */}
      {modalAcq && (
        <div className="bop-modal-overlay">
          <div className="bop-modal">
            <div className="bop-modal__header">
              <h3 className="bop-modal__title">
                {editingAcq ? 'Modifica acquirente' : 'Aggiungi acquirente'}
              </h3>
              <button className="bop-modal__close" onClick={() => setModalAcq(false)}>×</button>
            </div>
            <div className="bop-modal__body">
              {errorAcq && <div className="inline-error">{errorAcq}</div>}
              <div className="bop-form-row">
                <div className="bop-form-group">
                  <label className="bop-label">Unità (es. A/01) <span className="bop-text-danger">*</span></label>
                  <input
                    className="bop-input"
                    value={formAcq.unitaCodice}
                    onChange={e => setFormAcq(f => ({ ...f, unitaCodice: e.target.value }))}
                    placeholder="es. A/01"
                  />
                </div>
                <div className="bop-form-group">
                  <label className="bop-label">Descrizione unità</label>
                  <input
                    className="bop-input"
                    value={formAcq.unitaDescrizione}
                    onChange={e => setFormAcq(f => ({ ...f, unitaDescrizione: e.target.value }))}
                    placeholder="es. Piano terra int. 1"
                  />
                </div>
              </div>
              <div className="bop-form-row">
                <div className="bop-form-group">
                  <label className="bop-label">Nome <span className="bop-text-danger">*</span></label>
                  <input
                    className="bop-input"
                    value={formAcq.nome}
                    onChange={e => setFormAcq(f => ({ ...f, nome: e.target.value }))}
                  />
                </div>
                <div className="bop-form-group">
                  <label className="bop-label">Cognome <span className="bop-text-danger">*</span></label>
                  <input
                    className="bop-input"
                    value={formAcq.cognome}
                    onChange={e => setFormAcq(f => ({ ...f, cognome: e.target.value }))}
                  />
                </div>
              </div>
              <div className="bop-form-row">
                <div className="bop-form-group">
                  <label className="bop-label">Telefono</label>
                  <input
                    className="bop-input"
                    value={formAcq.telefono}
                    onChange={e => setFormAcq(f => ({ ...f, telefono: e.target.value }))}
                  />
                </div>
                <div className="bop-form-group">
                  <label className="bop-label">Email</label>
                  <input
                    className="bop-input"
                    type="email"
                    value={formAcq.email}
                    onChange={e => setFormAcq(f => ({ ...f, email: e.target.value }))}
                  />
                </div>
              </div>
              <div className="bop-form-group">
                <label className="bop-label">Note</label>
                <input
                  className="bop-input"
                  value={formAcq.note}
                  onChange={e => setFormAcq(f => ({ ...f, note: e.target.value }))}
                />
              </div>

              {editingAcq && (
                <>
                  <hr className="ofc-modal-divider" />
                  <GestioneAllegatiAcquirente
                    acquirenteId={editingAcq.id}
                    acquirenteUnita={editingAcq.unitaCodice}
                    acquirenteNomeCompleto={`${editingAcq.nome} ${editingAcq.cognome}`}
                    onChange={ricaricaAcquirenti}
                  />
                </>
              )}
            </div>
            <div className="bop-modal__footer">
              <button className="bop-btn bop-btn-secondary" onClick={() => setModalAcq(false)}>Annulla</button>
              <button className="bop-btn bop-btn-primary" onClick={salvaAcquirente} disabled={savingAcq}>
                {savingAcq ? 'Salvo…' : 'Salva'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modale dedicata allegati acquirente ── */}
      {modalAllegatiAcqId != null && (() => {
        const acq = acquirenti.find(a => a.id === modalAllegatiAcqId);
        if (!acq) return null;
        return (
          <ModaleAllegatiAcquirenteDedicata
            key={acq.id}
            acquirente={acq}
            onClose={() => setModalAllegatiAcqId(null)}
            onChange={ricaricaAcquirenti}
          />
        );
      })()}
    </>
  );
}

// ── Componente principale ─────────────────────────────────────────────────────

export default function SchedaCommessa() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [commessa, setCommessa] = useState<CommessaDettaglio | null>(null);
  const [loading, setLoading]   = useState(true);
  const [error,   setError]     = useState('');
  const [activeTab, setActiveTab] = useState(0);

  // Filtro acquirente condiviso tra Capitolato e Preventivi
  const [filtroAcquirentePrev, setFiltroAcquirentePrev] = useState<{
    id: number;
    label: string;
  } | null>(null);

  const apriPreventiviPerAcquirente = useCallback((acquirenteId: number, label: string) => {
    setFiltroAcquirentePrev({ id: acquirenteId, label });
    setActiveTab(1); // indice del tab "Preventivi"
  }, []);

  // Filtro acquirente condiviso con tab Ordini (Nodo 3)
  const [filtroAcquirenteOrd, setFiltroAcquirenteOrd] = useState<{
    id: number;
    label: string;
  } | null>(null);

  // Reso disponibile per un eventuale cablaggio da TabCapitolato (parallelo a apriPreventiviPerAcquirente)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const apriOrdiniPerAcquirente = useCallback((acquirenteId: number, label: string) => {
    setFiltroAcquirenteOrd({ id: acquirenteId, label });
    setActiveTab(2); // indice del tab "Ordini Cliente"
  }, []);

  // Modal Modifica
  const [modalModifica, setModalModifica] = useState(false);
  const [formMod, setFormMod] = useState<{
    nominativoId: number | null;
    descrizione: string;
    indirizzoLavori: string;
    responsabileId: number;
    budgetIniziale: string;
    sedeId: number;
    condizionePagamentoId: number | null;
  }>({
    nominativoId: null, descrizione: '',
    indirizzoLavori: '', responsabileId: 0, budgetIniziale: '',
    sedeId: 0, condizionePagamentoId: null,
  });
  const [savingMod, setSavingMod] = useState(false);
  const [modError,  setModError]  = useState('');
  const [sediOpt, setSediOpt] = useState<{ id: number; nome: string }[]>([]);
  const [utentiOpt, setUtentiOpt] = useState<{ id: number; nome: string; cognome?: string }[]>([]);
  const [condPagOpt, setCondPagOpt] = useState<{ id: number; nome: string; canale: string }[]>([]);

  // Modal Nuovo Preventivo
  const [modalNuovoPrev, setModalNuovoPrev] = useState(false);

  // Dirty-check per la modale "Modifica Commessa"
  const [initialSnapshot, setInitialSnapshot] = useState<string>('');
  const isDirty =
    modalModifica && initialSnapshot !== '' && JSON.stringify(formMod) !== initialSnapshot;
  useDirtyForm(isDirty, 'Scheda commessa');

  // Blocco cambio società mentre si è nella scheda
  useBloccoRecordContesto({
    recordSocietaId: commessa?.societaId,
    label: commessa ? `Commessa ${commessa.numero}` : 'Commessa',
  });

  // Modal Cambia Stato
  const [modalStato, setModalStato] = useState(false);
  const [nuovoStato, setNuovoStato] = useState<StatoCommessa | ''>('');
  const [savingStato, setSavingStato] = useState(false);
  const [statoError,  setStatoError]  = useState('');
  const [modalStampa, setModalStampa] = useState(false);

  // ── Caricamento ─────────────────────────────────────────────────────────────

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true); setError('');
    try {
      const data = await apiFetch<CommessaDettaglio>(`/api/commesse/${id}`);
      setCommessa(data);
    } catch (e: any) { setError(e.message); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // ── Modifica ─────────────────────────────────────────────────────────────────

  const apriModifica = async () => {
    if (!commessa) return;
    const datiIniziali = {
      nominativoId:          commessa.nominativoId ?? null,
      descrizione:           commessa.descrizione,
      indirizzoLavori:       commessa.indirizzoLavori ?? '',
      responsabileId:        commessa.responsabileId,
      budgetIniziale:        commessa.budgetIniziale != null ? String(commessa.budgetIniziale) : '',
      sedeId:                commessa.sedeId,
      condizionePagamentoId: commessa.condizionePagamentoId ?? null,
    };
    setFormMod(datiIniziali);
    setInitialSnapshot(JSON.stringify(datiIniziali));
    setModError(''); setModalModifica(true);

    try {
      const [resSedi, resUtenti, resCondPag] = await Promise.all([
        // @api GET /api/sedi/mie?societaId={id} -> {"success":"true","data":[{"id":"number","nome":"string"}]}
        apiFetch<{ success: boolean; data: { id: number; nome: string }[] }>(
          `/api/sedi/mie?societaId=${commessa.societaId}`
        ),
        // @api GET /api/utenti/selezionabili?societaId={id} -> {"success":"true","data":[{"id":"number","nome":"string"}]}
        apiFetch<{ success: boolean; data: { id: number; nome: string }[] }>(
          `/api/utenti/selezionabili?societaId=${commessa.societaId}`
        ),
        // @api GET /api/pagamenti?societaId={id}&attivo=true -> {"success":"true","data":[{"id":"number","nome":"string","canale":"string"}]}
        apiFetch<{ success: boolean; data: { id: number; nome: string; canale: string }[] }>(
          `/api/pagamenti?societaId=${commessa.societaId}&attivo=true`
        ),
      ]);
      setSediOpt(resSedi.data || []);
      setUtentiOpt(resUtenti.data || []);
      setCondPagOpt(resCondPag.data || []);
    } catch {
      // silent — i campi restano con opzioni vuote
    }
  };

  const salvaModifica = async () => {
    if (!commessa) return;
    setSavingMod(true); setModError('');
    try {
      // @api PUT /api/commesse/:id {"nominativoId":"number?","descrizione":"string?","indirizzoLavori":"string?","responsabileId":"number?","budgetIniziale":"number?","sedeId":"number?","condizionePagamentoId":"number?"} -> {"ok":"true"}
      await apiFetch(`/api/commesse/${commessa.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          nominativoId:          formMod.nominativoId,
          descrizione:           formMod.descrizione.trim(),
          indirizzoLavori:       formMod.indirizzoLavori.trim() || null,
          responsabileId:        formMod.responsabileId || null,
          budgetIniziale:        formMod.budgetIniziale ? Number(formMod.budgetIniziale) : null,
          sedeId:                formMod.sedeId || null,
          condizionePagamentoId: formMod.condizionePagamentoId,
        }),
      });
      setInitialSnapshot(JSON.stringify(formMod));
      setModalModifica(false); load();
    } catch (e: any) { setModError(e.message); }
    finally { setSavingMod(false); }
  };

  // ── Cambia Stato ────────────────────────────────────────────────────────────

  const confermaCambiaStato = async () => {
    if (!commessa || !nuovoStato) return;
    setSavingStato(true); setStatoError('');
    try {
      await apiFetch(`/api/commesse/${commessa.id}/stato`, {
        method: 'PUT',
        body: JSON.stringify({ stato: nuovoStato }),
      });
      setModalStato(false); load();
    } catch (e: any) { setStatoError(e.message); }
    finally { setSavingStato(false); }
  };

  // ── Render ───────────────────────────────────────────────────────────────────

  if (loading) {
    return <Layout><div className="inline-loading bop-scheda-commessa__loading">Caricamento commessa…</div></Layout>;
  }
  if (error || !commessa) {
    return (
      <Layout>
        <div className="inline-error bop-scheda-commessa__error">{error || 'Commessa non trovata'}</div>
        <BopBackLink to="/commerciale/commesse" label="Torna alle commesse" />
      </Layout>
    );
  }

  const sc = STATO_CONFIG[commessa.stato];
  const isBlocked = commessa.stato === 'COMPLETATA' || commessa.stato === 'CHIUSA';
  const transizioni = TRANSIZIONI[commessa.stato];

  return (
    <Layout>
      <BopBackLink to="/commerciale/commesse" label="Torna alle commesse" />
      {/* Breadcrumb */}
      <div className="bop-scheda-commessa__breadcrumb">
        <span className="bop-scheda-commessa__breadcrumb-link"
          onClick={() => navigate('/commerciale/commesse')}>Commesse</span>
        <span className="bop-scheda-commessa__breadcrumb-sep">›</span>
        <span className="bop-scheda-commessa__breadcrumb-current">{commessa.numero}</span>
      </div>

      {/* Header commessa */}
      <div className="bop-card bop-scheda-commessa__header-card">
        <div className="bop-scheda-commessa__header-top">
          <div className="bop-scheda-commessa__header-info">
            <div className="bop-scheda-commessa__numero-row">
              <span className="bop-scheda-commessa__numero">
                {commessa.numero}
              </span>
              <span className={`bop-badge ${sc.cls} bop-scheda-commessa__badge-lg`}>
                {sc.label}
              </span>
            </div>
            <div className="bop-scheda-commessa__cliente">
              {commessa.clienteRagioneSociale}
              {commessa.nominativoId && (
                <>
                  {' '}
                  <a
                    href={`/anagrafiche/nominativi/${commessa.nominativoId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bop-btn bop-btn-ghost"
                    title="Apri scheda cliente"
                  >
                    Apri Cliente
                  </a>
                </>
              )}
              {!commessa.nominativoId && (
                <>
                  {' '}
                  <span className="bop-badge bop-badge--warning" title="Cliente da agganciare">
                    Cliente non agganciato
                  </span>
                </>
              )}
            </div>
            <div className="bop-scheda-commessa__descrizione">{commessa.descrizione}</div>
            <div className="bop-scheda-commessa__meta">
              <span>{commessa.sedeNome ?? '—'}</span>
              <span>
                {commessa.responsabile
                  ? (commessa.responsabile.cognome
                      ? `${commessa.responsabile.nome} ${commessa.responsabile.cognome}`
                      : commessa.responsabile.nome)
                  : (commessa.responsabileNome ?? '—')}
              </span>
              {commessa.condizionePagamento && (
                <span>
                  {commessa.condizionePagamento.nome}
                  <span className="bop-text-muted"> ({commessa.condizionePagamento.canale})</span>
                </span>
              )}
              {commessa.indirizzoLavori && <span>{commessa.indirizzoLavori}</span>}
            </div>
          </div>

          <div className="bop-scheda-commessa__header-actions">
            <button className="bop-btn bop-btn-secondary" onClick={apriModifica}>Modifica</button>
            {transizioni.length > 0 && (
              <button className="bop-btn bop-btn-secondary"
                onClick={() => { setNuovoStato(''); setStatoError(''); setModalStato(true); }}>
                Cambia Stato
              </button>
            )}
            <button className="bop-btn bop-btn-secondary"
              onClick={() => { setActiveTab(11); }}>
              + Team
            </button>
            <button className="bop-btn bop-btn-secondary"
              onClick={() => setModalStampa(true)}>
              Stampa / Invia
            </button>
          </div>
        </div>

        {/* Banner stato */}
        {commessa.stato === 'COMPLETATA' && (
          <div className="bop-scheda-commessa__banner-warning">
            Commessa completata — nessuna nuova attività può essere aggiunta
          </div>
        )}
        {commessa.stato === 'CHIUSA' && (
          <div className="bop-scheda-commessa__banner-danger">
            Commessa chiusa — operazioni bloccate
          </div>
        )}
      </div>

      {/* Tab navigation */}
      <div className="bop-card bop-scheda-commessa__tab-card">
        <div className="bop-tabs bop-scheda-commessa__tab-nav">
          {TAB_LABELS.map((label, i) => (
            <button key={i} onClick={() => setActiveTab(i)}
              className={`bop-tab ${activeTab === i ? 'active' : ''}`}>
              {label}
            </button>
          ))}
        </div>

        <div className="bop-tab-content">
          {activeTab === 0  && <TabRiepilogo  commessa={commessa} onRefresh={load} />}
          {activeTab === 1  && (
            <TabPreventivi
              commessaId={commessa.id}
              societaId={commessa.societaId}
              filtroAcquirente={filtroAcquirentePrev}
              onResetFiltroAcquirente={() => setFiltroAcquirentePrev(null)}
              onNuovoPreventivo={() => setModalNuovoPrev(true)}
            />
          )}
          {activeTab === 2  && (
            <TabOrdini
              commessaId={commessa.id}
              societaId={commessa.societaId}
              filtroAcquirente={filtroAcquirenteOrd}
              onResetFiltroAcquirente={() => setFiltroAcquirenteOrd(null)}
            />
          )}
          {activeTab === 3  && (
            <TabAcconti
              commessaId={commessa.id}
              societaId={commessa.societaId}
              commessaNumero={commessa.numero}
            />
          )}
          {activeTab === 4  && <TabDistinte   commessa={commessa} onRefresh={load} isBlocked={isBlocked} />}
          {activeTab === 5  && <TabSal        commessa={commessa} onRefresh={load} isBlocked={isBlocked} />}
          {activeTab === 6  && (
            <TabFatture
              commessaId={commessa.id}
              societaId={commessa.societaId}
            />
          )}
          {activeTab === 7  && <TabPlaceholder label="Costi" />}
          {activeTab === 8  && <TabPlaceholder label="Magazzino" />}
          {activeTab === 9  && <TabPlaceholder label="Subappalti" />}
          {activeTab === 10 && <TabDocumenti  commessa={commessa} onRefresh={load} />}
          {activeTab === 11 && <TabTeam       commessa={commessa} onRefresh={load} />}
          {activeTab === 12 && (
            <TabCapitolato
              commessa={commessa}
              onApriPreventiviAcquirente={apriPreventiviPerAcquirente}
            />
          )}
        </div>
      </div>

      {/* ── Modal Modifica ── */}
      {modalModifica && (
        <div className="bop-modal-overlay">
          <div className="bop-modal bop-modal--sm">
            <div className="bop-modal__header">
              <h3 className="bop-modal__title">Modifica Commessa</h3>
              <button className="bop-modal__close"
                onClick={() => setModalModifica(false)}>×</button>
            </div>
            <div className="bop-modal__body">
              {modError && <div className="inline-error">{modError}</div>}
              <div className="bop-form-group">
                <label className="bop-label">Cliente <span className="bop-text-danger">*</span></label>
                <NominativoClientePicker
                  societaId={commessa.societaId}
                  value={formMod.nominativoId}
                  onChange={(nomId) => setFormMod(f => ({ ...f, nominativoId: nomId }))}
                  placeholder="Seleziona cliente…"
                />
                <small className="bop-text-muted">
                  I dati cliente freezati verranno aggiornati dal nominativo selezionato.
                </small>
              </div>
              <div className="bop-form-group">
                <label className="bop-label">Sede <span className="bop-text-danger">*</span></label>
                <select
                  className="bop-select"
                  value={formMod.sedeId || ''}
                  onChange={e => setFormMod(f => ({ ...f, sedeId: parseInt(e.target.value, 10) || 0 }))}
                >
                  <option value="">— Seleziona sede —</option>
                  {sediOpt.map(s => <option key={s.id} value={s.id}>{s.nome}</option>)}
                </select>
              </div>
              <div className="bop-form-group">
                <label className="bop-label">Descrizione</label>
                <textarea className="bop-input" rows={2} value={formMod.descrizione}
                  onChange={e => setFormMod(f => ({ ...f, descrizione: e.target.value }))} />
              </div>
              <div className="bop-form-group">
                <label className="bop-label">Indirizzo Lavori</label>
                <input className="bop-input" value={formMod.indirizzoLavori}
                  onChange={e => setFormMod(f => ({ ...f, indirizzoLavori: e.target.value }))} />
              </div>
              <div className="bop-form-group">
                <label className="bop-label">Responsabile <span className="bop-text-danger">*</span></label>
                <select
                  className="bop-select"
                  value={formMod.responsabileId || ''}
                  onChange={e => setFormMod(f => ({ ...f, responsabileId: parseInt(e.target.value, 10) || 0 }))}
                >
                  <option value="">— Seleziona responsabile —</option>
                  {utentiOpt.map(u => (
                    <option key={u.id} value={u.id}>
                      {u.cognome ? `${u.nome} ${u.cognome}` : u.nome}
                    </option>
                  ))}
                </select>
              </div>
              <div className="bop-form-group">
                <label className="bop-label">Condizione di pagamento</label>
                <select
                  className="bop-select"
                  value={formMod.condizionePagamentoId ?? ''}
                  onChange={e => setFormMod(f => ({
                    ...f,
                    condizionePagamentoId: e.target.value ? parseInt(e.target.value, 10) : null,
                  }))}
                >
                  <option value="">— Nessuna —</option>
                  {condPagOpt.map(cp => (
                    <option key={cp.id} value={cp.id}>{cp.nome} ({cp.canale})</option>
                  ))}
                </select>
              </div>
              <div className="bop-form-group">
                <label className="bop-label">Budget Iniziale (€)</label>
                <input className="bop-input" type="number" value={formMod.budgetIniziale}
                  onChange={e => setFormMod(f => ({ ...f, budgetIniziale: e.target.value }))} />
              </div>
            </div>
            <div className="bop-modal__footer">
              <button className="bop-btn bop-btn-secondary" onClick={() => setModalModifica(false)}>Annulla</button>
              <button className="bop-btn bop-btn-primary" onClick={salvaModifica} disabled={savingMod}>
                {savingMod ? 'Salvo…' : 'Salva'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal Cambia Stato ── */}
      {modalStato && (
        <div className="bop-modal-overlay">
          <div className="bop-modal bop-modal--sm">
            <div className="bop-modal__header">
              <h3 className="bop-modal__title">Cambia Stato</h3>
              <button className="bop-modal__close"
                onClick={() => setModalStato(false)}>×</button>
            </div>
            <div className="bop-modal__body">
              {statoError && <div className="inline-error">{statoError}</div>}
              <div className="bop-form-group">
                <label className="bop-label">Stato attuale</label>
                <div className="bop-scheda-commessa__stato-attuale">
                  <span className={`bop-badge ${sc.cls}`}>
                    {sc.label}
                  </span>
                </div>
              </div>
              <div className="bop-form-group">
                <label className="bop-label">Nuovo stato <span className="bop-text-danger">*</span></label>
                <select className="bop-input" value={nuovoStato}
                  onChange={e => setNuovoStato(e.target.value as StatoCommessa)}>
                  <option value="">Seleziona…</option>
                  {transizioni.map(s => (
                    <option key={s} value={s}>{STATO_CONFIG[s].label}</option>
                  ))}
                </select>
              </div>
              {nuovoStato === 'CHIUSA' && (
                <div className="bop-scheda-commessa__danger-box">
                  Attenzione: questa operazione è irreversibile. Il deposito commessa verrà azzerato e tutte le operazioni saranno bloccate definitivamente.
                </div>
              )}
            </div>
            <div className="bop-modal__footer">
              <button className="bop-btn bop-btn-secondary" onClick={() => setModalStato(false)}>Annulla</button>
              <button className="bop-btn bop-btn-primary" onClick={confermaCambiaStato}
                disabled={savingStato || !nuovoStato}>
                {savingStato ? 'Salvo…' : 'Conferma'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ── Modal Stampa / Invia ── */}
      {modalStampa && commessa && (
        <PrintModal
          societaId={commessa.societaId}
          entitaType="commessa"
          entitaId={commessa.id}
          entitaLabel={`Commessa ${commessa.numero}`}
          onClose={() => setModalStampa(false)}
        />
      )}

      {/* ── Modal Nuovo Preventivo ── */}
      {modalNuovoPrev && commessa && (
        <ModalNuovoPreventivo
          societaId={String(commessa.societaId)}
          onClose={() => setModalNuovoPrev(false)}
          onCreato={(id) => {
            setModalNuovoPrev(false);
            window.open(`/commerciale/attivo/preventivi/${id}`, '_blank', 'noopener,noreferrer');
          }}
          initialTipo="CONFIGURATO_COMMESSA"
          initialCommessa={{
            id: commessa.id,
            numero: commessa.numero,
            descrizione: commessa.descrizione,
          }}
        />
      )}
    </Layout>
  );
}
